import React, { useState } from "react";
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import axios from "axios";
import { SERVER_URL } from "../config";

const ChangePasswordModal = ({ isOpen, onClose, employee_id }) => {
    const [email, setEmail] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);

    const handleClose = () => {
        setEmail("");
        setNewPassword("");
        setConfirmPassword("");
        setShowNew(false);
        setShowConfirm(false);
        setError("");
        setSuccess("");
        onClose();
    };

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!email || !newPassword || !confirmPassword) {
            setError("All fields are required.");
            return;
        }

        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        try {
            setLoading(true);

            const res = await axios.post(
                `${SERVER_URL}/Rahul/changePassword.php`,
                {
                    email,
                    new_password: newPassword
                }
            );

            if (res.data.status === "success") {
                setSuccess("Password updated successfully");

                // ✅ CLEAR FORM DATA IMMEDIATELY
                setEmail("");
                setNewPassword("");
                setConfirmPassword("");
                setShowNew(false);
                setShowConfirm(false);

                // Close modal after a short delay
                setTimeout(() => {
                    onClose();
                    setSuccess("");
                }, 1500);
            } else {
                setError(res.data.message || "Failed to update password");
            }

        } catch (err) {
            setError("Server error. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
            onClick={handleClose}
        >
            <div
                className="bg-teal-100 w-[300px] h-[400px] rounded-2xl shadow-2xl p-5 border border-gray-100 flex flex-col justify-between overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div>
                    <div className="mb-4 text-center">
                        <h3 className="text-lg font-bold text-sky-800">
                            Change Password
                        </h3>
                    </div>

                    <div className="space-y-3">
                        {/* Email / Username */}
                        <input
                            type="text"
                            placeholder="Email / Username"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full h-10 px-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-teal-100"
                        />

                        {/* New Password */}
                        <div className="relative">
                            <input
                                type={showNew ? "text" : "password"}
                                placeholder="New Password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="w-full h-10 px-3 pr-10 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-teal-100"
                            />
                            <button
                                type="button"
                                onClick={() => setShowNew(!showNew)}
                                className="absolute right-0 top-0 h-full w-10 flex items-center justify-center text-gray-500 hover:text-indigo-600"
                            >
                                {showNew ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                            </button>
                        </div>

                        {/* Confirm Password */}
                        <div className="relative">
                            <input
                                type={showConfirm ? "text" : "password"}
                                placeholder="Confirm New Password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full h-10 px-3 pr-10 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-teal-100"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirm(!showConfirm)}
                                className="absolute right-0 top-0 h-full w-10 flex items-center justify-center text-gray-500 hover:text-indigo-600"
                            >
                                {showConfirm ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                            </button>
                        </div>

                        {/* Messages */}
                        <div className="min-h-[14px] text-center">
                            {error && <p className="text-[10px] font-medium text-red-500">{error}</p>}
                            {success && <p className="text-[10px] font-medium text-green-600">{success}</p>}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex flex-col gap-2 mt-auto">
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="w-full py-2.5 text-sm font-bold bg-cyan-300 text-white rounded-xl hover:bg-cyan-400 transition-all disabled:opacity-50"
                    >
                        {loading ? "Updating..." : "Update Password"}
                    </button>
                    <button
                        onClick={handleClose}
                        className="w-full py-1.5 text-xs font-semibold text-gray-600 hover:text-gray-800 bg-teal-100 hover:bg-teal-200 rounded-lg"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChangePasswordModal;
